import type { Interaction, InteractionStep } from "@/types";
import { clampPercentage, titleize } from "@/utils/formatters";

export function stepForFrame<T extends { screenshot_id?: string | null }>(
  steps: T[],
  screenshots: Array<{ id: string }>,
  frameIndex: number,
): T | undefined {
  const screenshotId = screenshots[frameIndex]?.id;
  if (!screenshotId) {
    return undefined;
  }

  const linked = steps.find((step) => step.screenshot_id === screenshotId);
  if (linked) {
    return linked;
  }

  const indexStep = steps[frameIndex];
  if (indexStep?.screenshot_id && indexStep.screenshot_id !== screenshotId) {
    return undefined;
  }

  return indexStep;
}

export function stepTitle(step: InteractionStep | undefined, index: number) {
  if (step?.title) {
    return step.title;
  }

  if (step?.action_type) {
    return titleize(step.action_type);
  }

  return `Step ${index + 1}`;
}

export function viewportLabel(viewport: string | null) {
  if (viewport === "mobile") {
    return "Mobile";
  }

  if (viewport === "desktop") {
    return "Desktop";
  }

  return viewport || "Unknown";
}

export function interactionStatusLabel(interaction: Interaction) {
  if (interaction.status === "running") {
    return "Running";
  }

  if (interaction.status === "pending") {
    return "On queue";
  }

  if (interaction.status === "failed") {
    return "Failed";
  }

  if (interaction.status === "cancelled") {
    return "Cancelled";
  }

  return "Completed";
}

export function maxStepError(interaction: Interaction) {
  return interaction.error_code === "max_steps" || interaction.error_code === "max_steps_reached";
}

export function boundingBoxCenter(boundingBox: number[] | null | undefined) {
  if (boundingBox?.length !== 4) {
    return null;
  }

  const [yMin, xMin, yMax, xMax] = boundingBox;

  return {
    x: clampPercentage((xMin + xMax) / 20),
    y: clampPercentage((yMin + yMax) / 20),
  };
}

/** Last screenshot % cursor at or before frameIndex (hold through scroll/wait frames). */
export function stickyCursorCenter(
  steps: Array<Pick<InteractionStep, "screenshot_id" | "bounding_box">>,
  screenshots: { id: string }[],
  frameIndex: number,
) {
  for (let index = frameIndex; index >= 0; index -= 1) {
    const step = stepForFrame(steps, screenshots, index);
    const center = boundingBoxCenter(step?.bounding_box);
    if (center) {
      return center;
    }
  }

  return null;
}

/** Map screenshot % coords through object-contain letterboxing into container %. */
export function objectContainCursorPosition({
  containerWidth,
  containerHeight,
  imageAspect,
  x,
  y,
}: {
  containerWidth: number;
  containerHeight: number;
  imageAspect: number;
  x: number;
  y: number;
}) {
  if (containerWidth <= 0 || containerHeight <= 0 || imageAspect <= 0) {
    return { x, y };
  }

  const containerAspect = containerWidth / containerHeight;
  const drawnWidth = containerAspect > imageAspect ? containerHeight * imageAspect : containerWidth;
  const drawnHeight = containerAspect > imageAspect ? containerHeight : containerWidth / imageAspect;
  const offsetX = (containerWidth - drawnWidth) / 2;
  const offsetY = (containerHeight - drawnHeight) / 2;

  return {
    x: ((offsetX + (x / 100) * drawnWidth) / containerWidth) * 100,
    y: ((offsetY + (y / 100) * drawnHeight) / containerHeight) * 100,
  };
}

/** Map a screenshot % rectangle through object-contain letterboxing into container %. */
export function objectContainRectPosition({
  containerWidth,
  containerHeight,
  imageAspect,
  xMin,
  yMin,
  xMax,
  yMax,
}: {
  containerWidth: number;
  containerHeight: number;
  imageAspect: number;
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
}) {
  const topLeft = objectContainCursorPosition({
    containerWidth,
    containerHeight,
    imageAspect,
    x: xMin,
    y: yMin,
  });
  const bottomRight = objectContainCursorPosition({
    containerWidth,
    containerHeight,
    imageAspect,
    x: xMax,
    y: yMax,
  });

  return {
    left: Math.min(topLeft.x, bottomRight.x),
    top: Math.min(topLeft.y, bottomRight.y),
    width: Math.abs(bottomRight.x - topLeft.x),
    height: Math.abs(bottomRight.y - topLeft.y),
  };
}

export function timelinePercentage(currentFrame: number, frameCount: number) {
  if (frameCount <= 1) {
    return 0;
  }

  return (currentFrame / (frameCount - 1)) * 100;
}
